import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ErrorMessage, Field, FieldArray, Form, Formik } from 'formik';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';

import { create, getById, update } from 'app/api/productApi';
import { getAllLightCompanies } from 'app/api/insuranceCompanyApi';
import { ProgressSpinner } from 'primereact/progressspinner';
import { getAllProductTypes } from 'app/api/productTypeApi';
import * as Yup from 'yup';
import { InputNumber } from 'primereact/inputnumber';
import { ToastContext } from 'app/context/ToastContext'; // Assuming you have this service setup

const validationSchema = Yup.object({
  insuranceCompany: Yup.object().required('Spoločnosť je povinný údaj'),
  name: Yup.string().required('Meno je povinný údaj').max(255, 'Max 255 znakov'),
  productType: Yup.object().required('Typ produktu je povinný údaj'),
});

const provisionTypes = [
  { value: 2, label: 'následná' },
  { value: 1, label: 'získateľská' },
];

const riskTypes = [
  { value: 1, label: 'hlavné riziko' },
  { value: 2, label: 'vedľajšie riziká' },
  { value: 3, label: 'Vedľajšie riziká 2' },
];

const internControl = [
  { value: 1, label: 'Podľa periodicity' },
  { value: 2, label: 'Podľa doby a pripoistenia' },
  { value: 3, label: 'Spolu za rok' },
];

const CreateProduct = () => {
  const { id } = useParams();

  const [searchParamsInitial] = useSearchParams();
  const tempParameters = searchParamsInitial.get('p');

  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [insuranceCompanies, setInsuranceCompanies] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const { showError } = useContext(ToastContext);

  useEffect(() => {
    getAllProductTypes().then(data => {
      setProductTypes(
        data.data.map(p => ({
          label: p.name,
          value: p.id,
        })),
      );
    });

    getAllLightCompanies().then(data => {
      setInsuranceCompanies(
        data.data.map(company => ({
          label: company.name,
          value: company.id,
        })),
      );
      if (id) {
        getById(id).then(pr => {
          if (!pr.data?.additionalInsurances || pr.data?.additionalInsurances?.length === 0) {
            pr.data.additionalInsurances = [{ name: '', description: '' }];
          }

          setProduct(pr.data);
          setLoading(false);
        });
      } else {
        setLoading(false);
      }
    });
  }, [id]);

  const handleSubmit = values => {
    if (id) {
      update(values)
        .then(() => navigate(`/products?p=${tempParameters}`))
        .catch(e => {
          console.error('Chyba pri úprave produktu:', e);
          showError('Chyba', e.response?.data?.detail ?? 'Nastala chyba pri ukladaní produktu.');
        });
    } else {
      create(values)
        .then(() => navigate(`/products?p=${tempParameters}`))
        .catch(e => {
          console.error('Chyba pri vytváraní produktu:', e);
          showError('Chyba', e.response?.data?.detail ?? 'Nastala chyba pri ukladaní produktu.');
        });
    }
  };

  return (
    <div className="card">
      {loading && (
        <div className="flex justify-content-center">
          <ProgressSpinner />
        </div>
      )}
      {!loading && (
        <>
          <h2>{id ? 'Upraviť dáta produktu' : 'Vytvoriť nový produkt'}</h2>
          <Formik
            initialValues={
              product
                ? product
                : {
                    insuranceCompany: product ? product.insuranceCompany?.id : '',
                    productType: product ? product.productType?.id : '',
                    name: product ? product.name : '',
                    additionalInsurances: product ? product.additionalInsurances : [{ name: '', description: '' }],
                  }
            }
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, setFieldValue, errors }) => {
              return (
                <Form
                  onKeyDown={e => {
                    if (e.key === 'Enter') e.preventDefault();
                  }}
                  className="p-fluid"
                >
                  <div className="grid">
                    <div className="col-12 md:col-6">
                      <div className="p-field">
                        <label htmlFor="insuranceCompany">Spoločnosť</label>
                        <Dropdown
                          id="insuranceCompany"
                          name="insuranceCompany"
                          value={values.insuranceCompany?.id}
                          options={insuranceCompanies}
                          optionLabel="label"
                          filter
                          onChange={e => void setFieldValue('insuranceCompany.id', e.value)}
                          placeholder="Vyberte si spoločnosť"
                        />
                        <ErrorMessage name="insuranceCompany" component="div" className="p-error" />
                      </div>
                    </div>

                    <div className="col-12 md:col-6">
                      <div className="p-field">
                        <label htmlFor="productType">Typ produktu</label>
                        <Dropdown
                          id="productType"
                          name="productType"
                          value={values.productType?.id}
                          options={productTypes}
                          optionLabel="label"
                          onChange={e => void setFieldValue('productType.id', e.value)}
                          placeholder="Vyberte si typ produktu"
                        />
                        <ErrorMessage name="productType" component="div" className="p-error" />
                      </div>
                    </div>
                    <div className="col-8">
                      <div className="p-field">
                        <label htmlFor="name">Názov produktu</label>
                        <Field name="name" as={InputText} />
                        <ErrorMessage name="name" component="div" className="p-error" />
                      </div>
                    </div>
                    <div className="col-4">
                      <div className="p-field">
                        <label htmlFor="name">Interná kontrola výšky provízie</label>
                        <Dropdown
                          id="provisionCheckType"
                          name="provisionCheckType"
                          value={values.provisionCheckType}
                          options={internControl}
                          optionLabel="label"
                          onChange={e => void setFieldValue('provisionCheckType', e.value)}
                          placeholder="Vyberte si typ kontroly"
                        />
                        <ErrorMessage name="name" component="div" className="p-error" />
                      </div>
                    </div>

                    <div style={{ width: '100%' }}>
                      <>
                        <div className="col-12">
                          <h3>Zoznam možných pripoistení:</h3>
                        </div>

                        <FieldArray name="additionalInsurances">
                          {({ insert, remove, push }) => (
                            <div>
                              {values.additionalInsurances.length > 0 &&
                                values.additionalInsurances.map((insurance, index) => (
                                  <div key={index + ' ' + (insurance?.id ? insurance.id : index)} className="flex p-mb-2">
                                    <div className="col-10">
                                      <div className="p-field">
                                        <Field name={`additionalInsurances.${index}.name`} as={InputText} placeholder="Meno" />
                                      </div>
                                    </div>
                                    <div className="col-2">
                                      <div className="p-field">
                                        <Button
                                          style={{ backgroundColor: '#e31818' }}
                                          icon="pi pi-trash"
                                          type="button"
                                          onClick={() => remove(index)}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                ))}

                              <div className="col-12" style={{ alignSelf: 'center', marginBottom: '20px' }}>
                                <div className="p-field">
                                  <Button
                                    style={{ backgroundColor: 'green' }}
                                    type="button"
                                    label="Pridať pripoistenie"
                                    onClick={() => push({ name: '', description: '' })}
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </FieldArray>
                      </>
                    </div>

                    <br />
                    <div className="p-field">
                      <div className="col-12">
                        <h3>Zoznam províznych schém pre produkt:</h3>
                      </div>
                      <FieldArray name="provisionSchemes">
                        {({ insert, remove, push }) => (
                          <div>
                            {values.provisionSchemes?.length > 0 &&
                              values.provisionSchemes
                                .sort((a, b) => a.id - b.id)
                                .map((insurance, index) => {
                                  return (
                                    <div key={insurance?.id} className="flex p-mb-2">
                                      <div className="col-6 md:col-1">
                                        <div className="p-field">
                                          {index === 0 && <label htmlFor={`provisionSchemes.${index}.id`}>ID</label>}
                                          <div id={`provisionSchemes.${index}.id`}>{insurance?.id}</div>
                                        </div>
                                      </div>
                                      <div className="col-6 md:col-2">
                                        <div className="p-field">
                                          {index === 0 && <label htmlFor={`provisionSchemes.${index}.risk`}>Typ rizika</label>}
                                          <Dropdown
                                            id={`provisionSchemes.${index}.risk`}
                                            name={`provisionSchemes.${index}.risk`}
                                            value={insurance?.risk}
                                            options={riskTypes}
                                            optionLabel="label"
                                            onChange={e => void setFieldValue(`provisionSchemes.${index}.risk`, e.value)}
                                            placeholder="Vyberte si riziko"
                                          />
                                        </div>
                                      </div>
                                      <div className="col-6 md:col-2">
                                        <div className="p-field">
                                          {index === 0 && <label htmlFor={`provisionSchemes.${index}.provisionType`}>Typ provizie</label>}
                                          <Dropdown
                                            id={`provisionSchemes.${index}.provisionType`}
                                            name={`provisionSchemes.${index}.provisionType`}
                                            value={insurance?.provisionType}
                                            options={provisionTypes}
                                            optionLabel="label"
                                            onChange={e => void setFieldValue(`provisionSchemes.${index}.provisionType`, e.value)}
                                            placeholder="Vyberte si riziko"
                                          />
                                        </div>
                                      </div>
                                      <div className="col-6 md:col-1">
                                        <div className="p-field">
                                          {index === 0 && <label htmlFor={`provisionSchemes.${index}.provision`}>Provizia (%)</label>}
                                          <InputNumber
                                            id={`provisionSchemes.${index}.provision`}
                                            value={insurance?.provision}
                                            locale="sk"
                                            minFractionDigits={0}
                                            maxFractionDigits={10}
                                            onValueChange={e => void setFieldValue(`provisionSchemes.${index}.provision`, e.value)}
                                          />
                                          <ErrorMessage name={`provisionSchemes.${index}.provision`} component="div" className="p-error" />
                                        </div>
                                      </div>
                                      {/*Poistne za poistenie*/}
                                      <div className="col-6 md:col-1">
                                        <div className="p-field">
                                          {index === 0 && <label htmlFor={`provisionSchemes.${index}.percentoDoSiete`}>Do siete (%)</label>}
                                          <InputNumber
                                            id={`provisionSchemes.${index}.percentoDoSiete`}
                                            value={insurance?.percentoDoSiete}
                                            locale="sk"
                                            minFractionDigits={0}
                                            maxFractionDigits={2}
                                            onValueChange={e => void setFieldValue(`provisionSchemes.${index}.percentoDoSiete`, e.value)}
                                          />
                                          <ErrorMessage
                                            name={`provisionSchemes.${index}.percentoDoSiete`}
                                            component="div"
                                            className="p-error"
                                          />
                                        </div>
                                      </div>
                                      {/*Doba poistenia*/}
                                      <div className="col-6 md:col-1">
                                        <div className="p-field">
                                          {index === 0 && (
                                            <label htmlFor={`provisionSchemes.${index}.fromYearContracts`}>Od roku zmluvy</label>
                                          )}
                                          <InputNumber
                                            id={`provisionSchemes.${index}.fromYearContracts`}
                                            value={insurance?.fromYearContracts}
                                            locale="sk"
                                            onValueChange={e => void setFieldValue(`provisionSchemes.${index}.fromYearContracts`, e.value)}
                                          />
                                          <ErrorMessage
                                            name={`provisionSchemes.${index}.fromYearContracts`}
                                            component="div"
                                            className="p-error"
                                          />
                                        </div>
                                      </div>
                                      <div className="col-6 md:col-1">
                                        <div className="p-field">
                                          {index === 0 && (
                                            <label htmlFor={`provisionSchemes.${index}.toYearContracts`}>Do roku zmluvy</label>
                                          )}
                                          <InputNumber
                                            id={`provisionSchemes.${index}.toYearContracts`}
                                            value={insurance?.toYearContracts}
                                            locale="sk"
                                            onValueChange={e => void setFieldValue(`provisionSchemes.${index}.toYearContracts`, e.value)}
                                          />
                                          <ErrorMessage
                                            name={`provisionSchemes.${index}.toYearContracts`}
                                            component="div"
                                            className="p-error"
                                          />
                                        </div>
                                      </div>

                                      <div className="col-6 md:col-1">
                                        <div className="p-field">
                                          {index === 0 && (
                                            <label htmlFor={`provisionSchemes.${index}.fromPeriodOfInsurance`}>Od doby poistenia</label>
                                          )}
                                          <InputNumber
                                            id={`provisionSchemes.${index}.fromPeriodOfInsurance`}
                                            value={insurance?.fromPeriodOfInsurance}
                                            locale="sk"
                                            onValueChange={e =>
                                              void setFieldValue(`provisionSchemes.${index}.fromPeriodOfInsurance`, e.value)
                                            }
                                          />
                                          <ErrorMessage
                                            name={`provisionSchemes.${index}.fromPeriodOfInsurance`}
                                            component="div"
                                            className="p-error"
                                          />
                                        </div>
                                      </div>

                                      <div className="col-6 md:col-1">
                                        <div className="p-field">
                                          {index === 0 && (
                                            <label htmlFor={`provisionSchemes.${index}.toPeriodOfInsurance`}>Do doby poistenia</label>
                                          )}
                                          <InputNumber
                                            id={`provisionSchemes.${index}.toPeriodOfInsurance`}
                                            value={insurance?.toPeriodOfInsurance}
                                            locale="sk"
                                            onValueChange={e =>
                                              void setFieldValue(`provisionSchemes.${index}.toPeriodOfInsurance`, e.value)
                                            }
                                          />
                                          <ErrorMessage
                                            name={`provisionSchemes.${index}.toPeriodOfInsurance`}
                                            component="div"
                                            className="p-error"
                                          />
                                        </div>
                                      </div>

                                      <div className="col-1" style={{ marginTop: index === 0 ? '40px' : '0px' }}>
                                        <div className="p-field">
                                          <Button
                                            style={{ backgroundColor: '#e31818' }}
                                            icon="pi pi-trash"
                                            severity="danger"
                                            type="button"
                                            onClick={() => remove(index)}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}

                            <div className="col-12" style={{ alignSelf: 'center', marginBottom: '20px' }}>
                              <div className="p-field">
                                <Button
                                  style={{ backgroundColor: 'green' }}
                                  type="button"
                                  label="Pridať proviznu schemu"
                                  onClick={() => push({})}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </FieldArray>
                      <div />
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '10px' }}>
                    <Button
                      style={{ backgroundColor: 'gray' }}
                      type="button"
                      label="Späť"
                      icon="pi pi-arrow-left"
                      className="p-button-secondary"
                      onClick={() => navigate(`/products?p=${tempParameters}`)}
                    />
                    <Button type="submit" label={id ? 'Uložiť produkt' : 'Uložiť produkt'} />
                  </div>
                </Form>
              );
            }}
          </Formik>{' '}
        </>
      )}
    </div>
  );
};

export default CreateProduct;
